/**
 * Missing bills by NMI (spec 2026-10-20): one row per expected NMI, one cell per
 * month, saying whether a bill covers that month. Pure — the service loads the
 * sites and tags, the board supplies the bills, and this builds the grid, the
 * per-month summary, the bulk-tag targets and the CSV lines.
 */
import type { BvBill } from '../types/billTypes';
import { isArchived } from '../utils/archiveScope';
import type { BoardRow } from './boardRows';
import type { BillFilters } from './billFilters';
import { addMonths, billInMonth, billMonthOptions, monthLabel, monthsInRange, type MonthOption } from './billMonths';
import { WORKFLOW_ORDER, type BillWorkflowStatus } from './billWorkflow';
import { effectiveInvoiceResult } from './effectiveInvoice';
import { matchesSiteFilters, siteFilterOptions, siteValue, type SiteFilterKey } from './siteFilters';
import { expectedInMonth, type SiteEnrolment } from './siteStatus';

export type MissingTag = 'chasing_retailer' | 'internal_review' | 'not_expected';

export const MISSING_TAGS: MissingTag[] = ['chasing_retailer', 'internal_review', 'not_expected'];

export const MISSING_TAG_LABELS: Record<MissingTag, string> = {
  chasing_retailer: 'Chasing retailer',
  internal_review: 'Needs internal review',
  not_expected: 'Not expected',
};

export const MAX_NMI_MONTHS = 24;

/** A row of the `site` select in services/missingBillService. */
export type ExpectedSite = {
  id: string;
  nmi: string | null;
  addr_street: string | null;
  addr_city: string | null;
  addr_state: string | null;
  current_retailer: string | null;
  bv_extn: (SiteEnrolment & { nmi_normalised?: string; network_distributor?: string; network_tariff_code?: string; metering_provider?: string }) | null;
  client: { name?: string | null } | null;
};

export type MissingTagRow = {
  id: string;
  site_id: string;
  /** `YYYY-MM-01`. */
  month: string;
  tag: MissingTag;
  note: string | null;
  updated_by_email: string | null;
  updated_at: string;
};

export type GridBill = {
  id: string;
  siteId: string;
  from: string | null;
  to: string | null;
  archivedAt: string | null;
  status: BillWorkflowStatus;
  /**
   * An invoice has arrived for this bill: something is attached (parse state
   * not 'none') or there is an invoice result. `missing_invoice` spans the
   * assigned, period, parsed and NMI stages, and only some of those mean no
   * invoice has come in.
   */
  hasInvoice: boolean;
};

export type MissingCell =
  | { kind: 'not_enrolled'; month: string }
  | { kind: 'no_bill'; month: string; tag: MissingTagRow | null }
  | { kind: 'missing_invoice'; month: string; billId: string; extra: number; tag: MissingTagRow | null }
  /** At `missing_invoice` but the invoice is in hand: has a bill, not missing, not taggable. */
  | { kind: 'received_unprocessed'; month: string; billId: string; extra: number }
  | { kind: 'billed'; month: string; billId: string; status: BillWorkflowStatus; extra: number };

export type MissingRow = {
  site: ExpectedSite;
  nmi: string;
  retailer: string;
  client: string;
  siteLabel: string;
  cells: MissingCell[];
  missingCount: number;
  undatedBills: number;
};

export type MonthSummary = { month: string; expected: number; billed: number; missing: number; notExpected: number };
export type MissingGrid = { months: string[]; rows: MissingRow[]; summary: MonthSummary[] };
export type NmiRange = { from: string; to: string; months: string[]; clamped: boolean };
/** `month` is `YYYY-MM`. */
export type TagCellRef = { siteId: string; month: string };

export function gridBillOf(row: Pick<BoardRow, 'bill' | 'status' | 'parseState'>): GridBill {
  const { bill } = row;
  return {
    id: bill.id,
    siteId: bill.site_id,
    from: bill.bill_from || null,
    to: bill.bill_to || null,
    archivedAt: bill.archived_at ?? null,
    status: row.status,
    hasInvoice: row.parseState !== 'none' || !!effectiveInvoiceResult(bill),
  };
}

export function siteNmi(site: ExpectedSite): string {
  return site.nmi || site.bv_extn?.nmi_normalised || '';
}

/** The site as the shared site filters read it (a bill's joined `site`). */
function subjectOf(site: ExpectedSite): Pick<BvBill, 'site'> {
  return {
    site: {
      nmi: site.nmi ?? undefined,
      addr_state: site.addr_state ?? undefined,
      current_retailer: site.current_retailer ?? undefined,
      bv_extn: site.bv_extn ?? undefined,
      client: site.client ? { name: site.client.name ?? undefined } : undefined,
    },
  };
}

/** Search and the five site filters only; verdict, status and send do not apply to a site. */
export function filterSites(sites: ExpectedSite[], filters: BillFilters): ExpectedSite[] {
  const q = filters.q.trim().toLowerCase();
  return sites.filter((site) => {
    if (q && !(
      siteNmi(site).toLowerCase().includes(q)
      || siteValue(subjectOf(site), 'entity').toLowerCase().includes(q)
    )) return false;
    return matchesSiteFilters(subjectOf(site), filters);
  });
}

export function siteOptions(sites: ExpectedSite[]): Record<SiteFilterKey, string[]> {
  return siteFilterOptions(sites.map(subjectOf));
}

/**
 * The months the grid shows. No end set: the last completed month. `from` only:
 * from there through the last completed month (just `from` if it is later).
 * `to` only: that single month. Reversed ends are swapped; more than
 * MAX_NMI_MONTHS keeps the months ending at the upper end.
 */
export function resolveNmiRange(from: string, to: string, lastCompleted: string): NmiRange {
  let lo: string;
  let hi: string;
  if (from && to) [lo, hi] = [from, to];
  else if (from) [lo, hi] = [from, from > lastCompleted ? from : lastCompleted];
  else if (to) [lo, hi] = [to, to];
  else [lo, hi] = [lastCompleted, lastCompleted];
  if (lo > hi) [lo, hi] = [hi, lo];
  let months = monthsInRange(lo, hi);
  const clamped = months.length > MAX_NMI_MONTHS;
  if (clamped) {
    months = months.slice(months.length - MAX_NMI_MONTHS);
    lo = months[0];
  }
  return { from: lo, to: hi, months, clamped };
}

/**
 * Month dropdown options, newest first: every month a bill touches, plus the
 * MAX_NMI_MONTHS months ending at the current one, so the default month is
 * always offered even when no bill touches it.
 */
export function nmiMonthOptions(
  bills: { bill_from?: string | null; bill_to?: string | null }[],
  lastCompleted: string,
): MonthOption[] {
  const current = addMonths(lastCompleted, 1);
  const keys = new Set(billMonthOptions(bills).map((o) => o.key));
  for (let i = 0; i < MAX_NMI_MONTHS; i += 1) keys.add(addMonths(current, -i));
  return [...keys].sort().reverse().map((key) => ({ key, label: monthLabel(key) }));
}

export function isMissing(cell: MissingCell): boolean {
  if (cell.kind !== 'no_bill' && cell.kind !== 'missing_invoice') return false;
  return cell.tag?.tag !== 'not_expected';
}

function isNotExpected(cell: MissingCell): boolean {
  return (cell.kind === 'no_bill' || cell.kind === 'missing_invoice') && cell.tag?.tag === 'not_expected';
}

function byRetailerThenClient(a: string, b: string): number {
  if (a === b) return 0;
  if (a === '') return 1;
  if (b === '') return -1;
  return a.localeCompare(b);
}

export function buildMissingGrid(input: {
  sites: ExpectedSite[];
  bills: GridBill[];
  tags: MissingTagRow[];
  months: string[];
}): MissingGrid {
  const { sites, bills, tags, months } = input;

  const activeBills = new Map<string, GridBill[]>();
  for (const b of bills) {
    if (isArchived({ archived_at: b.archivedAt })) continue;
    activeBills.set(b.siteId, [...(activeBills.get(b.siteId) ?? []), b]);
  }

  // One active tag per (site, month); if a feed ever holds two, the latest wins.
  const tagAt = new Map<string, MissingTagRow>();
  for (const t of tags) {
    const key = `${t.site_id}|${t.month.slice(0, 7)}`;
    const prev = tagAt.get(key);
    if (!prev || t.updated_at > prev.updated_at) tagAt.set(key, t);
  }

  const rows: MissingRow[] = [];
  for (const site of sites) {
    const own = activeBills.get(site.id) ?? [];
    const cells = months.map((month): MissingCell => {
      if (!expectedInMonth(site.bv_extn, month)) return { kind: 'not_enrolled', month };
      const tag = tagAt.get(`${site.id}|${month}`) ?? null;
      const covering = own.filter((b) => billInMonth({ bill_from: b.from, bill_to: b.to }, month));
      if (covering.length === 0) return { kind: 'no_bill', month, tag };
      const best = [...covering].sort((a, b) =>
        WORKFLOW_ORDER.indexOf(b.status) - WORKFLOW_ORDER.indexOf(a.status) || a.id.localeCompare(b.id))[0];
      const extra = covering.length - 1;
      if (best.status === 'missing_invoice') {
        // Arrived but not through the pipeline yet: not a missing invoice.
        if (best.hasInvoice) return { kind: 'received_unprocessed', month, billId: best.id, extra };
        return { kind: 'missing_invoice', month, billId: best.id, extra, tag };
      }
      return { kind: 'billed', month, billId: best.id, status: best.status, extra };
    });
    if (cells.every((c) => c.kind === 'not_enrolled')) continue;
    rows.push({
      site,
      nmi: siteNmi(site),
      retailer: (site.current_retailer ?? '').trim(),
      client: (site.client?.name ?? '').trim(),
      siteLabel: [site.addr_street, site.addr_city].filter(Boolean).join(', '),
      cells,
      missingCount: cells.filter(isMissing).length,
      undatedBills: own.filter((b) => !b.from || !b.to).length,
    });
  }

  rows.sort((a, b) =>
    Number(b.missingCount > 0) - Number(a.missingCount > 0)
    || byRetailerThenClient(a.retailer, b.retailer)
    || byRetailerThenClient(a.client, b.client)
    || a.nmi.localeCompare(b.nmi));

  const summary = months.map((month, i): MonthSummary => {
    const cells = rows.map((r) => r.cells[i]);
    return {
      month,
      expected: cells.filter((c) => c.kind !== 'not_enrolled').length,
      // "With a bill": received but not yet processed counts as having one.
      billed: cells.filter((c) => c.kind === 'billed' || c.kind === 'received_unprocessed').length,
      missing: cells.filter(isMissing).length,
      notExpected: cells.filter(isNotExpected).length,
    };
  });

  return { months, rows, summary };
}

/** With `onlyGaps`, just the rows that still have a missing month. */
export function visibleRows(rows: MissingRow[], onlyGaps: boolean): MissingRow[] {
  return onlyGaps ? rows.filter((r) => r.missingCount > 0) : rows;
}

/**
 * Every missing month of the selected rows (Not expected included — a bulk tag
 * overwrites it), never a billed or not-enrolled one. `overwrites` counts the
 * active tags that would change to a different value.
 */
export function bulkTagTargets(
  rows: MissingRow[],
  siteIds: ReadonlySet<string>,
  tag: MissingTag,
): { cells: TagCellRef[]; overwrites: number } {
  const cells: TagCellRef[] = [];
  let overwrites = 0;
  for (const row of rows) {
    if (!siteIds.has(row.site.id)) continue;
    for (const cell of row.cells) {
      if (cell.kind !== 'no_bill' && cell.kind !== 'missing_invoice') continue;
      cells.push({ siteId: row.site.id, month: cell.month });
      if (cell.tag && cell.tag.tag !== tag) overwrites += 1;
    }
  }
  return { cells, overwrites };
}

/** A bulk tag is sent in calls of at most this many cells, to keep each RPC payload bounded. */
export const TAG_CELLS_PER_CALL = 2000;

/** `items` in order, in chunks of at most `size`. */
export function chunked<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export const MISSING_CSV_HEADERS = [
  'Retailer', 'Client', 'Site', 'NMI', 'Month', 'Tag', 'Note', 'Last tagged by', 'Last tagged at',
] as const;

/** One line per missing NMI-month; Client is the client (entity) name. */
export function missingCsvRows(grid: MissingGrid): Record<string, string>[] {
  const lines: Record<string, string>[] = [];
  for (const row of grid.rows) {
    for (const cell of row.cells) {
      if (!isMissing(cell) || (cell.kind !== 'no_bill' && cell.kind !== 'missing_invoice')) continue;
      lines.push({
        Retailer: row.retailer,
        Client: row.client,
        Site: row.siteLabel,
        NMI: row.nmi,
        Month: cell.month,
        Tag: cell.tag ? MISSING_TAG_LABELS[cell.tag.tag] : '',
        Note: cell.tag?.note ?? '',
        'Last tagged by': cell.tag?.updated_by_email ?? '',
        'Last tagged at': cell.tag?.updated_at ?? '',
      });
    }
  }
  return lines;
}
